import React, { useEffect } from "react";

const BRAND = "#87CEEB";
const BRAND_DARK = "#4FA3C7";
const INK = "#0F2742";

const HEADINGS = [
  { size: "text-5xl", label: "H1 · 48px" },
  { size: "text-4xl", label: "H2 · 36px" },
  { size: "text-2xl", label: "H3 · 24px" },
  { size: "text-lg", label: "H4 · 18px" },
];

const SAMPLE = {
  big: "Real support. Real people.",
  medium: "Find support, fast",
  small: "Easy as 1·2·3",
  body:
    "BTR Health connects NDIS participants directly with verified support workers — no middlemen, no surprise fees.",
};

const Column = ({ title, fontStyle, accent }) => (
  <div className="flex-1 rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_-12px_rgba(15,39,66,0.15)]">
    <div className="mb-6 flex items-center gap-3">
      <div
        className="h-3 w-3 rounded-full"
        style={{ background: accent }}
      />
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {title}
      </div>
    </div>

    {/* Big hero heading */}
    <div className="mb-8">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-slate-400">
        Hero · 48–60px · 700
      </div>
      <h1
        className="leading-[1.05] tracking-tight"
        style={{ ...fontStyle, fontSize: "3.5rem", fontWeight: 700, color: INK }}
      >
        Real support.
        <br />
        <span style={{ color: BRAND_DARK }}>Real people.</span>
      </h1>
    </div>

    <div className="my-6 h-px bg-slate-100" />

    {/* H2 */}
    <div className="mb-6">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-slate-400">
        H2 · 36px · 700
      </div>
      <h2
        className="leading-tight tracking-tight"
        style={{ ...fontStyle, fontSize: "2.25rem", fontWeight: 700, color: INK }}
      >
        Find support, fast
      </h2>
    </div>

    {/* H3 */}
    <div className="mb-6">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-slate-400">
        H3 · 24px · 600
      </div>
      <h3
        className="leading-tight"
        style={{ ...fontStyle, fontSize: "1.5rem", fontWeight: 600, color: INK }}
      >
        Easy as 1 · 2 · 3
      </h3>
    </div>

    {/* H4 */}
    <div className="mb-6">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-slate-400">
        H4 · 18px · 600
      </div>
      <h4
        style={{ ...fontStyle, fontSize: "1.125rem", fontWeight: 600, color: INK }}
      >
        Verified, screened workers
      </h4>
    </div>

    {/* Body sample for context (kept in Public Sans either way) */}
    <div className="mb-6">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-slate-400">
        Body · Public Sans (unchanged)
      </div>
      <p
        className="leading-relaxed text-slate-600"
        style={{ fontFamily: "'Public Sans', sans-serif", fontSize: "1rem" }}
      >
        {SAMPLE.body}
      </p>
    </div>

    {/* Button preview */}
    <div className="mb-2">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-slate-400">
        Button label
      </div>
      <button
        className="rounded-full px-6 py-3 text-white shadow-lg"
        style={{
          ...fontStyle,
          fontWeight: 600,
          background: BRAND_DARK,
          boxShadow: "0 10px 30px -10px rgba(75,163,199,0.6)",
        }}
      >
        I'm looking for support
      </button>
    </div>

    {/* Alphabet specimen */}
    <div className="mt-8 rounded-2xl bg-slate-50 p-4">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-slate-400">
        Specimen
      </div>
      <div
        className="text-xl leading-snug"
        style={{ ...fontStyle, fontWeight: 500, color: INK }}
      >
        ABCDEFGHIJKLMNOPQRSTUVWXYZ
        <br />
        abcdefghijklmnopqrstuvwxyz
        <br />
        0123456789 — & ! ? @
      </div>
    </div>
  </div>
);

export default function FontComparePage() {
  useEffect(() => {
    // Inject Glacial Indifference (Google Fonts doesn't host it, use cdnfonts.com)
    const id = "glacial-indifference-cdn";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.cdnfonts.com/css/glacial-indifference-2";
      document.head.appendChild(link);
    }
  }, []);

  return (
    <main
      className="min-h-screen px-6 py-16 sm:px-10 lg:px-16"
      style={{ background: "linear-gradient(180deg,#EAF6FC 0%, #FFFFFF 60%)" }}
      data-testid="font-compare-page"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <span
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em]"
            style={{ background: "#EAF6FC", color: BRAND_DARK }}
          >
            Type comparison
          </span>
          <h1
            className="mt-5 text-4xl font-black tracking-tight sm:text-5xl"
            style={{ fontFamily: "'Outfit', sans-serif", color: INK }}
          >
            Outfit vs Glacial Indifference
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600" style={{ fontFamily: "'Public Sans', sans-serif" }}>
            Same headings, same colors, same brand. Only the heading typeface
            changes. Body text stays Public Sans in both versions.
          </p>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row">
          <Column
            title="Current — Outfit"
            accent={BRAND_DARK}
            fontStyle={{ fontFamily: "'Outfit', sans-serif" }}
          />
          <Column
            title="Option — Glacial Indifference"
            accent="#7B61FF"
            fontStyle={{ fontFamily: "'Glacial Indifference', sans-serif" }}
          />
        </div>

        {/* Notes */}
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-100">
            <h3
              className="text-lg font-bold"
              style={{ fontFamily: "'Outfit', sans-serif", color: INK }}
            >
              Outfit (current)
            </h3>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600" style={{ fontFamily: "'Public Sans', sans-serif" }}>
              <li>• Geometric, friendly, modern feel</li>
              <li>• Wide range of weights (100–900) on Google Fonts</li>
              <li>• Slightly playful — good for consumer brands</li>
              <li>• Hosted by Google Fonts (fast CDN, SLA)</li>
            </ul>
          </div>
          <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-100">
            <h3
              className="text-lg font-bold"
              style={{ fontFamily: "'Glacial Indifference', sans-serif", color: INK }}
            >
              Glacial Indifference (option)
            </h3>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600" style={{ fontFamily: "'Public Sans', sans-serif" }}>
              <li>• Cleaner, more minimal, slightly editorial</li>
              <li>• Only 3 weights (Regular, Bold, Italic) — limited range</li>
              <li>• Feels calmer and more premium / clinical</li>
              <li>• Not on Google Fonts — served via cdnfonts.com</li>
            </ul>
          </div>
        </div>

        <div className="mt-10 rounded-2xl border-2 border-dashed p-6 text-center" style={{ borderColor: BRAND }}>
          <p className="text-sm text-slate-600" style={{ fontFamily: "'Public Sans', sans-serif" }}>
            Like Glacial Indifference? Reply with <strong>"switch to Glacial Indifference"</strong> and I'll
            update the whole site's heading font in <code>tailwind.config.js</code> + <code>index.css</code>.
          </p>
        </div>
      </div>
    </main>
  );
}
